import React, {ChangeEvent, useState, useEffect, FormEvent, useRef} from 'react';
import {useLoaderData, useNavigate} from 'react-router-dom';
import AnimationScreen from '../components/AnimationScreen';
import ProgressBar from '../components/ProgressBar';
import NavigationBar from '../components/NavigationBar';
import { text, unitList } from '../data';
import { PageData } from '../types/types';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import { BearingInfo, TimkenUnit } from '../../components/bearing-search/types/types';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import { getPartNumberFromUrl, getContactSiteUrl } from '../../engineering-tools/utils/engineering-tools';
import PartPreSearch from '../../engineering-tools/components/PartPreSearch';

const BearingSearch: React.FC = () => {
	const handleNextPageRef = useRef<any>(null);
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const { title, progress, prev, content } = useLoaderData() as PageData;
	const { form } = content;
	const [ nextPage, setNextPage ] = useState<string | undefined>( undefined );
	const [ partNumber, setPartNumber ] = useState( '' );
	const [ searchNumber, setSearchNumber ] = useState<string | undefined>( undefined );
	const [ verifiedPart, setVerifiedPart ] = useState<string | undefined>( undefined );
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ units, setUnits ] = useState('M');
	const [ partPreSearchItemSelected, setPartPreSearchItemSelected ] = useState( false );

	useEffect( () => {
		if ( form?.path && verifiedPart ) {
			let path = `/${ form.path }/${ encodeURIComponent( verifiedPart ) }/${ units }`;
			setNextPage( path );
			if ( partPreSearchItemSelected ) {
				handleNextPageRef.current?.handleNextPage(path);
			}
		} else {
			setNextPage( undefined );
		}
	}, [ verifiedPart, units ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		if ( ! form ) {
			return;
		}

		const value = event.target.value;
		setVerifiedPart( undefined );
		setIsPartSelected( false );
		setPartNumber( value );
	};

	const inputUnit = ( event: ChangeEvent<HTMLSelectElement>) => {
		if ( ! form ) {
			return;
		}

		const value = event.target.value;
		setUnits( value );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();
		setSearchNumber( partNumber );
		setUnits( units );
		setIsPartOpen( true );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			const { marketPlaceDescription } = part;
			setVerifiedPart( marketPlaceDescription );
			setIsPartSelected( true );
		}

		setPartNumber( part?.marketPlaceDescription || '' );
		setIsPartOpen( false );
		setUnits( units );
	};

	const clearSearch = () => {
		setPartPreSearchItemSelected( false );
		setVerifiedPart( undefined );
		setPartNumber( '' );
		setIsPartSelected( false );
	};

	useEffect(() => {
		const partNumberFromUrl = getPartNumberFromUrl();
		if (partNumberFromUrl) {
			setPartNumber(partNumberFromUrl);
			setVerifiedPart(partNumberFromUrl);
			setIsPartSelected( true );
		}
	}, []);

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
		setPartPreSearchItemSelected ( true );
	};

	const setPartNumberAndVerifiedPart = (value) => {
		setVerifiedPart( value );
		setPartNumber( value );
	};

	return (
		<AnimationScreen>
			<ProgressBar progress={progress} />
			<h3>{title}</h3>
			<p className="c-guided-help__description">
				{text.contactText}&nbsp;
				<a href={getContactSiteUrl()} data-link-click>
					{text.contactForm}
				</a>
				{' form.'}
			</p>
			{form && (
				<>
					<div className="c-guided-help__form-subtitle">{form.title}</div>
					<form className="c-guided-help__search" onSubmit={handleSubmit}>
						<div className="c-guided-help__search timken-tool-presearch-container">
							<TextInput
								id={form.id}
								required={true}
								label={form.label}
								verified={!! verifiedPart}
								name="partNumber"
								value={partNumber}
								disabled={!! verifiedPart}
								placeholder={form.placeholder}
								message={verifiedPart ? '' : text.partWildCardHint}
								className="c-guided-help__input"
								onChange={inputPartNumber}
							/>
							<PartPreSearch
								input={partNumber}
								isHidden={partNumber.length < 2 || isPartOpen || isPartSelected}
								unit={(units as "M" | "I")}
								onClick={preSearchClickHandler}
								verifyPartNumber={(value: string) => setPartNumberAndVerifiedPart(value)}
							/>

							<SelectDropdown
								id="initial_grease_fill_unit"
								name="unit"
								required={true}
								label={text.displayUnits}
								value={units}
								options={unitList}
								onChange={inputUnit}
							/>

							{! verifiedPart && (
								<button type="submit" className="btn btn--primary" disabled={! partNumber}>
									{text.lookup}
								</button>
							)}

							{!! verifiedPart && (
								<button type="button" className="btn btn--secondary" onClick={() => clearSearch()}>
									{text.clear}
								</button>
							)}
						</div>
					</form>

					{isPartOpen && !! searchNumber && (
						<PartSearch units={units as 'M' | 'I'} partNumber={searchNumber} onSelect={verifyPart} onClose={() => setIsPartOpen( false )} />
					)}
				</>
			)}
			<NavigationBar prev={prev} next={nextPage} ref={handleNextPageRef} />
		</AnimationScreen>
	);
};

export default BearingSearch;
